import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiCopy, FiTrash2, FiMail, FiHeart } from 'react-icons/fi';
import toast from 'react-hot-toast';
import Footer from '../Layout/Footer';

function getSavedReplies() {
  try {
    return JSON.parse(localStorage.getItem('rc_saved_replies') || '[]');
  } catch { return []; }
}

const TONE_COLORS = {
  professional: '#3b82f6',
  warm: '#f59e0b',
  assertive: '#ef4444',
  casual: '#22c55e',
  diplomatic: '#8b5cf6'
};

export default function SavedReplies() {
  const navigate = useNavigate();
  const [replies, setReplies] = useState([]);
  const [copiedIdx, setCopiedIdx] = useState(null);

  useEffect(() => {
    setReplies(getSavedReplies());
  }, []);

  const handleCopy = (text, idx) => {
    navigator.clipboard.writeText(text);
    setCopiedIdx(idx);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopiedIdx(null), 2000);
  };

  const handleDelete = (idx) => {
    const updated = replies.filter((_, i) => i !== idx);
    localStorage.setItem('rc_saved_replies', JSON.stringify(updated));
    setReplies(updated);
    toast.success('Reply removed');
  };

  const handleOpenInEmail = (text) => {
    window.open(`mailto:?body=${encodeURIComponent(text)}`, '_blank');
  };

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <button onClick={() => navigate('/')} style={styles.backBtn}>
          <FiArrowLeft size={18} /> Back to Chat
        </button>

        <h1 style={styles.title}>
          <FiHeart size={24} /> Saved Replies
        </h1>
        <p style={styles.subtitle}>Your favorite replies, saved for quick reuse. ({replies.length} saved)</p>

        {replies.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>💾</div>
            <h3 style={{ color: 'var(--text-primary)', marginBottom: 8 }}>No saved replies yet</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>
              Click the heart icon on any generated reply to save it here for later use.
            </p>
          </div>
        ) : (
          <div style={styles.repliesList}>
            {replies.map((reply, idx) => {
              const color = TONE_COLORS[reply.tone] || 'var(--accent)';
              return (
                <div key={idx} style={styles.replyCard}>
                  <div style={styles.replyHeader}>
                    <div style={styles.headerLeft}>
                      <span style={{ ...styles.toneBadge, background: color + '22', color }}>
                        {reply.tone?.charAt(0).toUpperCase() + reply.tone?.slice(1)}
                      </span>
                      <span style={styles.date}>
                        {new Date(reply.savedAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div style={styles.actions}>
                      <button onClick={() => handleOpenInEmail(reply.content)} style={styles.actionBtn} title="Open in email">
                        <FiMail size={14} />
                      </button>
                      <button onClick={() => handleCopy(reply.content, idx)} style={styles.actionBtn} title="Copy">
                        {copiedIdx === idx ? <FiCopy size={14} color="var(--success)" /> : <FiCopy size={14} />}
                      </button>
                      <button onClick={() => handleDelete(idx)} style={{ ...styles.actionBtn, color: '#ef4444' }} title="Delete">
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                  </div>
                  <div style={styles.replyContent}>
                    {reply.content}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: 'var(--bg-primary)', display: 'flex', flexDirection: 'column' },
  container: { maxWidth: 700, margin: '0 auto', padding: '40px 20px', flex: 1 },
  backBtn: { display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: 14, cursor: 'pointer', marginBottom: 24 },
  title: { display: 'flex', alignItems: 'center', gap: 10, fontSize: 28, fontWeight: 700, marginBottom: 8, color: 'var(--text-primary)' },
  subtitle: { fontSize: 14, color: 'var(--text-muted)', marginBottom: 32 },
  emptyState: { textAlign: 'center', padding: '60px 20px', background: 'var(--bg-secondary)', borderRadius: 16, border: '1px solid var(--border)' },
  repliesList: { display: 'flex', flexDirection: 'column', gap: 16 },
  replyCard: { background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' },
  replyHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid var(--border)' },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 10 },
  toneBadge: { padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600 },
  date: { fontSize: 12, color: 'var(--text-muted)' },
  actions: { display: 'flex', gap: 4 },
  actionBtn: { display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 12, padding: '4px 8px', borderRadius: 6 },
  replyContent: { padding: 16, color: 'var(--text-primary)', fontSize: 14, lineHeight: 1.7, whiteSpace: 'pre-wrap' }
};
